// MiniKen Design Studio · Open — root shell.
//
// Routing logic (no client-router needed yet — single-page state machine):
//   1. On mount, hydrate UserSettings from IDB.
//   2. While hydrating, show a tiny splash so we don't flash the
//      onboarding wizard at users who already finished setup.
//   3. If setupComplete is false, mount OnboardingWizard.
//   4. Otherwise mount the main shell (placeholder for now; chat panel +
//      canvas + inspector arrive in later phases).
//
// SettingsPanel is mounted as an overlay reachable from the TopBar gear.

import { useEffect, useState } from 'react';

import { Canvas } from './components/Canvas';
import { ChatPanel } from './components/ChatPanel';
import { CostBadge } from './components/CostBadge';
import { CostToast } from './components/CostToast';
import { NoProjectScreen } from './components/NoProjectScreen';
import { OnboardingWizard } from './components/OnboardingWizard';
import { ProjectMenu } from './components/ProjectMenu';
import { SettingsPanel } from './components/SettingsPanel';
import { sendMessage } from './core/agent/orchestrator';
import { useCost } from './state/cost';
import { useProjects } from './state/projects';
import { useSettings } from './state/settings';
import { useTranscript } from './state/transcript';

export function App() {
  const hydrating = useSettings((s) => s.hydrating);
  const settings = useSettings((s) => s.settings);
  const hydrate = useSettings((s) => s.hydrate);
  const hydrateCost = useCost((s) => s.hydrate);
  const projectsHydrating = useProjects((s) => s.hydrating);
  const activeProjectName = useProjects((s) => s.activeProjectName);
  const hydrateProjects = useProjects((s) => s.hydrate);
  const bindTranscriptToProject = useTranscript((s) => s.bindProject);
  const [settingsOpen, setSettingsOpen] = useState(false);

  useEffect(() => {
    void hydrate();
    void hydrateCost();
    void hydrateProjects();
  }, [hydrate, hydrateCost, hydrateProjects]);

  // Re-bind transcript whenever the active project changes. bindProject
  // is a no-op when the name matches what's already bound, so this is
  // safe to fire on every render — but useEffect on activeProjectName
  // is more obvious to the next reader.
  useEffect(() => {
    if (activeProjectName) {
      bindTranscriptToProject(activeProjectName);
    }
  }, [activeProjectName, bindTranscriptToProject]);

  const handleSend = (text: string) => {
    if (!activeProjectName) return;
    void sendMessage({
      text,
      projectName: activeProjectName,
      settings,
    });
  };

  if (hydrating || projectsHydrating) {
    return (
      <main className="flex min-h-screen items-center justify-center text-[12px] text-neutral-600">
        載入中…
      </main>
    );
  }

  if (!settings.setupComplete) {
    return <OnboardingWizard onComplete={() => void 0} />;
  }

  if (!activeProjectName) {
    return (
      <>
        <NoProjectScreen />
        <CostToast />
      </>
    );
  }

  return (
    <>
      <main className="flex h-screen flex-col">
        <header className="flex shrink-0 items-center justify-between border-b border-[#222230] bg-[#0d0d12] px-5 py-2.5">
          <div className="flex items-center gap-3">
            <h1 className="miniken-wordmark text-[16px] leading-none">
              MiniKen<span className="period">.</span>
              <span className="ml-2 align-middle text-[11px] font-medium tracking-[0.04em] text-[#9090A8]">
                Design Studio · Open
              </span>
            </h1>
            <span className="text-neutral-700">·</span>
            <ProjectMenu />
          </div>
          <div className="flex items-center gap-3">
            <CostBadge />
            <span className="text-[11px] text-neutral-500">
              {settings.displayName} · {settings.providerSlug}
            </span>
            <button
              type="button"
              onClick={() => setSettingsOpen(true)}
              className="rounded-md border border-[#222230] bg-[#141419] px-2.5 py-1 text-[11px] text-neutral-300 hover:border-[#333348]"
              aria-label="設定"
            >
              ⚙ 設定
            </button>
          </div>
        </header>

        <div className="flex min-h-0 flex-1">
          <aside className="flex w-[460px] shrink-0 border-r border-[#222230]">
            <ChatPanel onSend={handleSend} />
          </aside>
          <section className="flex flex-1">
            <Canvas projectName={activeProjectName} />
          </section>
        </div>
      </main>
      {settingsOpen && <SettingsPanel onClose={() => setSettingsOpen(false)} />}
      <CostToast />
    </>
  );
}
